import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { OrbMark, BrandWordmark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PUB = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const VENUE_TYPES = ["Fine dining / tasting menu", "Casual dining", "Café / coffee shop", "Bar / lounge", "QSR / takeaway", "Hotel / venue group", "Other"];

export default function Landing() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [form, setForm] = useState({ venue_name: "", owner_name: "", email: "", phone: "", restaurant_type: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    PUB.get("/public/site").then((r) => setSite(r.data)).catch(() => setSite({ pricing_tiers: [], marketing_points: [] }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await PUB.post("/public/signup", form);
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07050A] text-[#EDEDF2]" data-testid="landing-page">
      <header className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between">
        <BrandWordmark size={30} sub />
        <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="landing-signin"
          className="border-white/15 text-white hover:bg-white/5">Sign in</Button>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#C9A8FF]/80 mb-6">Prepared Intelligence</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Every moving part of your restaurant,<br />
            <span className="text-[#9D5CFF]">run by one intelligence.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#BDB8CC] max-w-xl leading-relaxed">
            Rosters, leave, clock-ins, revenue, stock, recipes, functions, payroll and HR paperwork —
            one system that already knows how a restaurant actually runs. Built in South Africa, for South African hospitality.
          </p>
          <div className="mt-10 flex gap-3 flex-wrap">
            <Button onClick={() => document.getElementById("get-started")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="landing-get-started" className="bg-primary hover:bg-[#8B5CF6] text-white h-12 px-7 text-base">
              Get started <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button variant="outline" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="landing-pricing-link" className="border-white/15 text-white hover:bg-white/5 h-12 px-7 text-base">
              See pricing
            </Button>
          </div>
        </div>
        <div className="hidden lg:flex justify-center"><OrbMark size={320} animated /></div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="font-heading text-lg md:text-xl font-semibold text-[#C9A8FF] mb-8">What MISE handles for you</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-capabilities">
          {(site?.marketing_points || []).map((p, i) => (
            <div key={i} className="glass rounded-xl p-5 border border-white/5 hover:border-[#9D5CFF]/30 transition-colors">
              <CheckCircle size={20} className="text-[#A9C97E] mb-3" />
              <p className="text-sm leading-relaxed text-[#D8D4E3]">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="font-heading text-lg md:text-xl font-semibold text-[#C9A8FF] mb-2">Pricing</h2>
        <p className="text-sm text-[#79728C] mb-8">Simple tiers — final pricing confirmed during onboarding.</p>
        <div className="grid md:grid-cols-3 gap-5" data-testid="landing-pricing">
          {(site?.pricing_tiers || []).map((t) => (
            <div key={t.id} className="glass rounded-2xl p-7 border border-white/5 flex flex-col" data-testid={`landing-tier-${t.id}`}>
              <p className="font-heading text-lg font-bold">{t.name}</p>
              <p className="font-heading text-2xl font-bold text-[#9D5CFF] mt-1" data-testid={`landing-tier-price-${t.id}`}>{t.price_label}</p>
              <p className="text-xs text-[#79728C] mt-2">{t.blurb}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {(t.features || []).map((f, i) => (
                  <li key={i} className="text-sm text-[#D8D4E3] flex gap-2"><CheckCircle size={16} className="text-[#A9C97E] mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section id="get-started" className="max-w-6xl mx-auto px-6 pb-28">
        <div className="glass rounded-2xl border border-white/8 p-8 sm:p-10 max-w-2xl mx-auto" data-testid="landing-signup">
          <h2 className="font-heading text-lg md:text-xl font-semibold mb-1">Get started</h2>
          <p className="text-sm text-[#79728C] mb-6">Tell us about your venue — every application is reviewed personally before your account is created.</p>
          {done ? (
            <div className="text-center py-6 space-y-3" data-testid="landing-signup-done">
              <CheckCircle size={40} className="text-[#A9C97E] mx-auto" />
              <p className="font-heading font-semibold">Application received</p>
              <p className="text-sm text-[#79728C]">We'll review it and email you an activation link once approved.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {err && <p className="sm:col-span-2 text-sm text-[#F87171]" data-testid="landing-signup-error">{err}</p>}
              <div className="space-y-1.5">
                <Label className="text-xs">Venue name *</Label>
                <Input required value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} data-testid="signup-venue" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Your name *</Label>
                <Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="signup-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="signup-phone" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Type of venue</Label>
                <Select value={form.restaurant_type} onValueChange={(v) => setForm({ ...form, restaurant_type: v })}>
                  <SelectTrigger data-testid="signup-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{VENUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} data-testid="signup-submit"
                className="sm:col-span-2 bg-primary hover:bg-[#8B5CF6] text-white h-12 text-base">
                {busy ? "Submitting…" : "Apply for access"}
              </Button>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-[#79728C]">
        MISE — Prepared Intelligence · Cape Town, South Africa
      </footer>
    </div>
  );
}
